<template>
  <div class="detail-card" data-card-id="enhancement">
    <div
      class="card-header"
      :class="{ 'slidein-header': mode === 'draggable' }"
    >
      <h5 class="card-title">
        <a
          v-if="mode === 'parked'"
          href="#"
          @click.prevent="enhanceImageOpen = !enhanceImageOpen"
          class="section-toggle"
        >
          <font-awesome-icon
            :icon="
              enhanceImageOpen
                ? ['fas', 'chevron-down']
                : ['fas', 'chevron-right']
            "
            class="me-2"
          />
          Enhance Image
        </a>
        <span v-else>Enhance Image</span>
      </h5>
      <div v-if="mode === 'draggable'" class="header-right">
        <button
          class="drag-toggle-btn"
          @click.prevent="$emit('toggleDraggable')"
          title="Dock to panel"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>
      <div v-else class="header-right">
        <button
          class="drag-toggle-btn"
          @click.prevent="$emit('toggleDraggable')"
          title="Make draggable"
        >
          <font-awesome-icon :icon="['fas', 'clone']" />
        </button>
      </div>
    </div>

    <div v-if="enhanceImageOpen || mode === 'draggable'" class="card-content">
      <div class="enhancement-control">
        <div class="control-icon">
          <font-awesome-icon :icon="['fas', 'sun']" title="Brightness" />
        </div>
        <input
          :value="brightness"
          @input="$emit('update:brightness', parseFloat($event.target.value))"
          type="range"
          min="0"
          max="200"
          class="enhancement-slider"
        />
        <span class="control-value">{{ brightness }}%</span>
      </div>
      <div class="enhancement-control">
        <div class="control-icon">
          <font-awesome-icon :icon="['fas', 'adjust']" title="Contrast" />
        </div>
        <input
          :value="contrast"
          @input="$emit('update:contrast', parseFloat($event.target.value))"
          type="range"
          min="0"
          max="200"
          class="enhancement-slider"
        />
        <span class="control-value">{{ contrast }}%</span>
      </div>
      <div class="enhancement-control">
        <div class="control-icon">
          <font-awesome-icon :icon="['fas', 'tint']" title="Saturation" />
        </div>
        <input
          :value="saturate"
          @input="$emit('update:saturate', parseFloat($event.target.value))"
          type="range"
          min="0"
          max="200"
          class="enhancement-slider"
        />
        <span class="control-value">{{ saturate }}%</span>
      </div>
      <div class="enhancement-control">
        <div class="control-icon">
          <font-awesome-icon :icon="['fas', 'camera']" title="Sepia" />
        </div>
        <input
          :value="sepia"
          @input="$emit('update:sepia', parseFloat($event.target.value))"
          type="range"
          min="0"
          max="100"
          class="enhancement-slider"
        />
        <span class="control-value">{{ sepia }}%</span>
      </div>
      <div class="enhancement-control">
        <div class="control-icon">
          <font-awesome-icon :icon="['fas', 'fill']" title="Invert" />
        </div>
        <input
          :value="invert"
          @input="$emit('update:invert', parseFloat($event.target.value))"
          type="range"
          min="0"
          max="100"
          class="enhancement-slider"
        />
        <span class="control-value">{{ invert }}%</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";

const props = defineProps({
  brightness: Number,
  contrast: Number,
  saturate: Number,
  sepia: Number,
  invert: Number,
  mode: {
    type: String,
    default: "parked",
  },
});

defineEmits([
  "toggleDraggable",
  "update:brightness",
  "update:contrast",
  "update:saturate",
  "update:sepia",
  "update:invert",
]);

const enhanceImageOpen = ref(false);

// When switching to draggable, force open
watch(
  () => props.mode,
  (newMode) => {
    if (newMode === "draggable") {
      enhanceImageOpen.value = true;
    }
  }
);
</script>

<style scoped>
/* Enhancement Controls */
.enhancement-control {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--color-divider-navy);
}

.enhancement-control:last-child {
  border-bottom: none;
}

.control-icon {
  color: var(--color-accent-primary);
  font-size: 1rem;
  width: 24px;
  text-align: center;
  flex-shrink: 0;
}

.enhancement-slider {
  flex: 1;
  height: 6px;
  background: var(--color-bg-primary);
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.enhancement-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: var(--color-accent-primary);
  cursor: pointer;
  border-radius: 50%;
  border: 2px solid var(--color-bg-primary);
}

.enhancement-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: var(--color-accent-primary);
  cursor: pointer;
  border-radius: 50%;
  border: 2px solid var(--color-bg-primary);
}

.enhancement-slider:hover::-webkit-slider-thumb {
  background: var(--color-accent-hover);
}

.enhancement-slider:hover::-moz-range-thumb {
  background: var(--color-accent-hover);
}

.control-value {
  color: var(--color-text-secondary);
  font-size: 0.85rem;
  min-width: 50px;
  text-align: right;
  flex-shrink: 0;
}
</style>
